<template>
  <div class="cr-page">
    <SnackbarComp :SnackbarComponent="snackbar" />

    <div class="cr-panel">
      <div class="cr-panel__toolbar">
        <div class="cr-panel__toolbar-start">
          <span class="cr-panel__count">
            {{ members.length }} {{ members.length === 1 ? 'member' : 'members' }}
          </span>
        </div>
        <div class="pl-toolbar-actions" aria-label="Team actions">
          <div class="pl-toolbar-actions__buttons">
            <v-btn
              variant="flat"
              size="small"
              class="pl-toolbar-btn pl-toolbar-btn--primary"
              prepend-icon="mdi-account-plus"
              :disabled="!canManage"
              @click="openCreate"
            >
              Add member
            </v-btn>
            <v-btn
              variant="outlined"
              size="small"
              class="pl-toolbar-btn pl-toolbar-btn--ghost"
              prepend-icon="mdi-file-upload-outline"
              :disabled="!canManage"
              @click="bulkDialogOpen = true"
            >
              Bulk upload
            </v-btn>
            <v-btn
              variant="outlined"
              size="small"
              class="pl-toolbar-btn pl-toolbar-btn--ghost"
              prepend-icon="mdi-refresh"
              :loading="loading"
              @click="load"
            >
              Refresh
            </v-btn>
          </div>
        </div>
      </div>

      <v-alert v-if="!canManage" type="info" variant="tonal" class="teams-note">
        Only an owner or admin can change the team directory.
      </v-alert>

      <div class="cr-list">
        <div class="cr-list__head">
          <div class="cr-list__columns teams-columns">
            <span>Name</span>
            <span>Email</span>
            <span>Contact number</span>
            <span>Actions</span>
          </div>
        </div>

        <div class="cr-list__scroll">
          <div v-if="loading" class="cr-list__body">
            <div v-for="i in 4" :key="i" class="cr-row cr-row--skeleton teams-row" />
          </div>

          <div v-else-if="!members.length" class="cr-empty">
            <v-icon icon="mdi-account-group-outline" size="40" class="cr-empty__icon" />
            <p class="cr-empty__title">No team members yet</p>
            <p class="cr-empty__subtitle">Add one to get started.</p>
          </div>

          <div v-else class="cr-list__body">
            <div v-for="item in paginatedMembers" :key="item.id" class="cr-row teams-row">
              <div class="cr-row__name">
                <span class="cr-row__field-label">Name</span>
                <span class="cr-row__name-value">
                  {{ item.name }}
                  <span v-if="item.linkedToAccount" class="teams-linked-badge" title="Also an Admin/Agent account">
                    <v-icon icon="mdi-shield-account-outline" size="12" />
                    Admin/Agent
                  </span>
                </span>
              </div>
              <div class="cr-row__contact">
                <v-icon icon="mdi-email-outline" size="14" class="cr-row__contact-icon" />
                <span class="cr-row__contact-value">{{ item.email }}</span>
              </div>
              <div class="cr-row__contact">
                <v-icon icon="mdi-phone-outline" size="14" class="cr-row__contact-icon" />
                <span class="cr-row__contact-value">{{ item.contactNumber }}</span>
              </div>
              <div class="cr-row__actions">
                <v-menu location="bottom end">
                  <template #activator="{ props: menuProps }">
                    <button type="button" class="cr-actions-trigger" v-bind="menuProps" :disabled="!canManage">
                      <v-icon icon="mdi-dots-vertical" size="18" />
                    </button>
                  </template>
                  <div class="cr-actions-menu">
                    <button type="button" class="cr-actions-menu__item" @click="openEdit(item)">
                      <span class="cr-actions-menu__icon cr-actions-menu__icon--edit">
                        <v-icon icon="mdi-pencil-outline" size="18" />
                      </span>
                      <span class="cr-actions-menu__text">
                        <span class="cr-actions-menu__title">Edit</span>
                        <span class="cr-actions-menu__desc">Update name, email or number</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      class="cr-actions-menu__item cr-actions-menu__item--danger"
                      :disabled="item.linkedToAccount"
                      @click="confirmDelete(item)"
                    >
                      <span class="cr-actions-menu__icon cr-actions-menu__icon--delete">
                        <v-icon icon="mdi-trash-can-outline" size="18" />
                      </span>
                      <span class="cr-actions-menu__text">
                        <span class="cr-actions-menu__title">Remove</span>
                        <span class="cr-actions-menu__desc">
                          {{
                            item.linkedToAccount
                              ? 'Remove their Admin/Agent account instead'
                              : 'Take them off the team directory'
                          }}
                        </span>
                      </span>
                    </button>
                  </div>
                </v-menu>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PortalListPagination
        class="cr-panel__footer"
        :page="page"
        :page-count="pageCount"
        :items-per-page="itemsPerPage"
        :range-label="rangeLabel"
        :total="members.length"
        @update:page="setPage"
        @update:items-per-page="setItemsPerPage"
      />
    </div>

    <v-dialog v-model="dialogOpen" max-width="480" class="caller-dialog-root">
      <div class="caller-dialog">
        <div class="caller-dialog__header">
          <div class="caller-dialog__brand">
            <span class="caller-dialog__icon">
              <v-icon icon="mdi-account-plus-outline" color="white" size="20" />
            </span>
            <div>
              <p class="caller-dialog__title">{{ editingId ? 'Edit team member' : 'Add team member' }}</p>
              <p class="caller-dialog__subtitle">People the AI can contact during a call.</p>
            </div>
          </div>
          <button type="button" class="caller-dialog__close" @click="dialogOpen = false">
            <v-icon icon="mdi-close" size="20" />
          </button>
        </div>
        <div class="caller-dialog__body">
          <v-form ref="formRef" @submit.prevent="save">
            <div class="caller-field">
              <label class="caller-field__label">Name</label>
              <v-text-field
                v-model="form.name"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                :rules="[(v) => !!String(v || '').trim() || 'Name is required']"
                autofocus
              />
            </div>
            <div class="caller-field">
              <label class="caller-field__label">Email</label>
              <v-text-field
                v-model="form.email"
                type="email"
                variant="outlined"
                density="comfortable"
                hint="Used when a document is emailed, or a meeting invite is sent."
                persistent-hint
                :rules="[
                  (v) => !!String(v || '').trim() || 'Email is required',
                  (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v || '').trim()) || 'Enter a valid email',
                ]"
              />
            </div>
            <div class="caller-field">
              <label class="caller-field__label">Contact number</label>
              <v-text-field
                v-model="form.contactNumber"
                variant="outlined"
                density="comfortable"
                hint="Used to match this person on a call, and to send over WhatsApp."
                persistent-hint
                :rules="[
                  (v) => !!String(v || '').trim() || 'Contact number is required',
                  (v) => String(v || '').replace(/\D/g, '').length >= 10 || 'Enter at least 10 digits',
                ]"
              />
            </div>
          </v-form>
        </div>
        <div class="caller-dialog__footer">
          <v-btn variant="text" class="caller-dialog__cancel" :disabled="saving" @click="dialogOpen = false">Cancel</v-btn>
          <v-btn variant="flat" class="caller-dialog__submit" :loading="saving" @click="save">Save</v-btn>
        </div>
      </div>
    </v-dialog>

    <BulkAddTeamMembersDialog v-model="bulkDialogOpen" @saved="onBulkSaved" />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import PortalListPagination from '@/components/Extras/PortalListPagination.vue'
import BulkAddTeamMembersDialog from '@/components/Teams/BulkAddTeamMembersDialog.vue'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import {
  fetchTeamMembers,
  createTeamMember,
  editTeamMember,
  deleteTeamMember,
} from '@/api/teamMembersApi'

defineOptions({ name: 'TeamsPage' })

const store = useStore()
const members = ref([])
const loading = ref(false)
const saving = ref(false)
const deletingId = ref('')
const dialogOpen = ref(false)
const bulkDialogOpen = ref(false)
const editingId = ref('')
const formRef = ref(null)
const snackbar = ref({})
const form = reactive({ name: '', email: '', contactNumber: '' })

const page = ref(1)
const itemsPerPage = ref(25)

const pageCount = computed(() => {
  const perPage = itemsPerPage.value
  if (perPage <= 0) return 1
  return Math.max(1, Math.ceil(members.value.length / perPage))
})

const paginatedMembers = computed(() => {
  const perPage = itemsPerPage.value
  if (perPage <= 0) return members.value
  const start = (page.value - 1) * perPage
  return members.value.slice(start, start + perPage)
})

const rangeLabel = computed(() => {
  const total = members.value.length
  if (!total) return '0 of 0'
  const perPage = itemsPerPage.value
  if (perPage <= 0) return `1-${total} of ${total}`
  const start = (page.value - 1) * perPage + 1
  const end = Math.min(page.value * perPage, total)
  return `${start}-${end} of ${total}`
})

function setPage(next) {
  page.value = Math.min(Math.max(1, next), pageCount.value)
}

function setItemsPerPage(value) {
  itemsPerPage.value = value
  page.value = 1
}

// A delete (or a refetch that shrinks the list) can leave page pointed past
// the last real page -- pull it back rather than showing an empty page with
// working prev/next controls that look broken.
watch(members, () => {
  if (page.value > pageCount.value) {
    page.value = pageCount.value
  }
})

const canManage = computed(() =>
  ['OWNER', 'ADMIN'].includes(String(store.state.userRole ?? '').toUpperCase()),
)

function showSnack(type, text) {
  snackbar.value = { type, text, show: true }
}

async function load() {
  loading.value = true
  try {
    const response = await fetchTeamMembers()
    if (!isApiSuccess(response)) {
      showSnack('error', getApiMessage(response) || 'Could not load the team directory.')
      return
    }
    members.value = response?.data?.items ?? []
  } finally {
    loading.value = false
  }
}

function onBulkSaved() {
  load()
}

function openCreate() {
  editingId.value = ''
  Object.assign(form, { name: '', email: '', contactNumber: '' })
  dialogOpen.value = true
}

function openEdit(item) {
  editingId.value = item.id
  Object.assign(form, {
    name: item.name,
    email: item.email,
    contactNumber: item.contactNumber,
  })
  dialogOpen.value = true
}

async function save() {
  const validation = await formRef.value?.validate?.()
  if (validation && validation.valid === false) return

  saving.value = true
  try {
    const response = editingId.value
      ? await editTeamMember({ id: editingId.value, ...form })
      : await createTeamMember({ ...form })

    if (!isApiSuccess(response)) {
      // The backend rejects duplicate emails and numbers by name ("Priya
      // Sharma already uses that email address") -- surface that verbatim
      // rather than a generic failure, since it tells the user who to look for.
      showSnack('error', getApiMessage(response) || 'Could not save this team member.')
      return
    }
    showSnack('success', response?.status_message || 'Saved.')
    dialogOpen.value = false
    await load()
  } finally {
    saving.value = false
  }
}

async function confirmDelete(item) {
  if (!canManage.value || deletingId.value || item.linkedToAccount) return
  const ok = window.confirm(
    `Remove ${item.name} from the team directory?\n\n` +
      'Any on-call action set to contact them will stop being able to.',
  )
  if (!ok) return

  deletingId.value = item.id
  try {
    const response = await deleteTeamMember({ id: item.id })
    if (!isApiSuccess(response)) {
      showSnack('error', getApiMessage(response) || 'Could not remove this team member.')
      return
    }
    showSnack('success', response?.status_message || 'Removed.')
    await load()
  } finally {
    deletingId.value = ''
  }
}

onMounted(load)
</script>

<style scoped>
.teams-columns,
.teams-row:not(.cr-row--skeleton) {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1fr) 56px;
}

.teams-row.cr-row--skeleton {
  min-height: 44px;
}

.teams-note {
  margin: 10px 14px 0;
  flex-shrink: 0;
}

.teams-linked-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 8px;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.1);
  vertical-align: middle;
}
</style>
